import {
  Avatar,
  Icon,
  type IconType,
  isValidIconType,
} from '@assembly-js/design-system';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { ChannelListItem } from 'src/components/ChannelListItem';
import RightClickMenu from 'src/components/RightClickContextMenu';
import { InboxPageContext } from 'src/context/inboxPageContext';
import { NotificationEventType } from 'src/entities/Notifications';
import {
  useDeleteNotificationMutation,
  useUpdateNotificationMutation,
} from 'src/services/api/inboxApi';
import { getInitials } from 'src/utils/UserUtils';

import { ItemRenderProps } from '../Channels/BaseChannelSidebar';
import { AvatarInfoType } from '../Channels/ChannelSidebarItem';

import { InboxSidebarItemData } from './InboxSidebar';

const useStyles = makeStyles()((theme) => ({
  summaryText: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(0.5),
    '& > *:last-child': {
      flex: 1,
    },
  },
}));

export const getSidebarTitle = (title: string) =>
  title.charAt(0).toUpperCase() +
  (title.length > 1 ? title.slice(1).toLowerCase() : '');

// App-ready notifications store a design-system icon name in imageUrl. Avatar
// src would request that name as a relative page URL and server-render a 404.
export const getInboxAvatarImageProps = (
  imageUrl: string | undefined,
): { src: string } | { icon: IconType } => {
  const value = imageUrl ?? '';
  if (isValidIconType(value)) {
    return { icon: value };
  }
  if (isInboxAvatarImageSrc(value)) {
    return { src: value };
  }
  return { src: '' };
};

const isInboxAvatarImageSrc = (value: string) =>
  value.startsWith('https://') ||
  value.startsWith('http://') ||
  value.startsWith('/') ||
  value.startsWith('data:');

export const InboxSidebarItem: React.FC<
  ItemRenderProps<InboxSidebarItemData>
> = (props) => {
  const { selectedNotification } = React.useContext(InboxPageContext);
  const { channelOption, onSelect } = props;
  const { classes } = useStyles();
  const {
    avatarInfo,
    isRead,
    title,
    id: notificationId,
    notificationEventType,
  } = channelOption;
  const [updateNotification] = useUpdateNotificationMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const isCompanyNotification =
    avatarInfo?.isCompanyUser && avatarInfo?.companyName;
  const sidebarTitle =
    notificationEventType === 'internalChat.mentioned' ||
    notificationEventType === 'channel.mentioned'
      ? title
      : title.charAt(0).toUpperCase() +
        (title.length > 1 ? title.slice(1).toLowerCase() : '');

  const handleChannelClicked = () => {
    onSelect(channelOption);
  };

  const subTitleRenderer: React.FC<React.PropsWithChildren> = ({
    children,
  }) => {
    return (
      <div className={classes.summaryText}>
        <Avatar
          size="xs"
          {...getInboxAvatarImageProps(avatarInfo?.imageUrl)}
          text={getInitials(avatarInfo?.companyName || avatarInfo?.name || '', {
            twoLetters: !isCompanyNotification,
          })}
          fallbackColor={avatarInfo?.fallbackColor || ''}
          variant={
            avatarInfo
              ? getAvatarVariant(avatarInfo, notificationEventType)
              : 'circle'
          }
        />
        <>{children}</>
      </div>
    );
  };

  /**
   * This function gets the avatar variant based on the notification subtitle.
   * When the sender belongs to a company we append 'from' to the sender name
   * which is available in the avatarInfo.name property. The app-ready
   * notification also gets the rounded (app-icon) variant even though it
   * isn't a company sender — there is no user sender for it at all.
   * @param avatarData : sender avatar data
   * @param eventType : the notification's event type
   * @returns avatar variant
   */
  const getAvatarVariant = (
    avatarData: AvatarInfoType,
    eventType: NotificationEventType,
  ) =>
    avatarData.isCompanyUser || eventType === 'customApp.buildComplete'
      ? 'rounded'
      : 'circle';

  const markAsUnreadAction = {
    name: 'Mark as unread',
    icon: <Icon icon="EmailUnread" className="h-[13px] w-[13px]" />,
    onClick: () => {
      if (notificationId) {
        updateNotification({
          id: notificationId,
          isRead: false,
        });
      }
    },
  };

  const markAsReadAction = {
    name: 'Mark as read',
    icon: <Icon icon="EmailRead" className="h-[13px] w-[13px]" />,
    onClick: () => {
      if (notificationId) {
        updateNotification({
          id: notificationId,
          isRead: true,
        });
      }
    },
  };

  const deleteNotificationAction = {
    name: 'Delete notification',
    icon: <Icon icon="Trash" className="h-[13px] w-[13px]" />,
    isDelete: true,
    onClick: () => {
      if (notificationId) {
        deleteNotification({
          id: notificationId,
        });
      }
    },
  };

  return (
    <RightClickMenu
      actions={[
        isRead ? markAsUnreadAction : markAsReadAction,
        deleteNotificationAction,
      ]}
    >
      <ChannelListItem
        title={sidebarTitle}
        subTitleRenderer={subTitleRenderer}
        subTitle={`${avatarInfo?.name || ''}${
          avatarInfo?.companyName ? ` from ${avatarInfo?.companyName}` : ''
        }`}
        timestamp={channelOption.timestamp}
        selected={selectedNotification?.id === notificationId}
        onSelect={handleChannelClicked}
        unread={!isRead}
        isNotification={true}
      />
    </RightClickMenu>
  );
};
